import React, { useState } from 'react'
import useForm from 'react-hook-form'
import Swal from 'sweetalert2'
import * as yup from 'yup'
import Done from '../Done/Done'
import { locale, t } from '../../i18n'
import route from '../../utils/route'
import { alertProp } from '../../utils/swal'
import FormError from '../FormError/FormError'
import InputCesta from '../inputCesta/InputCesta'
import SpinnerWithText from '../spinnerWithText/SpinnerWithText'
import styles from './index.module.scss'
import { RequestTyreData } from '../../types/Neumatico'
import Size from '../../utils/media-queries-setup'
import DropDown from '../dropDown/DropDown'
import { RequestTyreMotoData } from './RequestTyreMoto'
import EmailIcon from './assets/email_icon.svg'
import NeumaticoIcon from './assets/neumatico_icon.svg'

export type RequestAvailabilityParams = {
  email: string
  selectedNeumatico: RequestTyreData | RequestTyreMotoData
  device: 'MOBILE' | 'TABLET' | 'DESKTOP'
  precio: string
  idioma: string
  cantidad: number
}

type RequestAvailabilityFunction = (
  params: RequestAvailabilityParams
) => Promise<void>

interface EmailData {
  email: string
  cantidad: number
  checkboxPrivacy: boolean
}

const EmailSchema = yup.object().shape({
  email: yup
    .string()
    .email(t('validations.register.valid_email'))
    .required(
      t('validations.register.required', { label: t('formLabels.email') })
    ),
  checkboxPrivacy: yup
    .boolean()
    .oneOf([true], t('validations.register.accept_terms')),
})

interface Props {
  selectedNeumatico: RequestTyreData | RequestTyreMotoData
  successMessage: boolean
  setSuccessMessage: React.Dispatch<React.SetStateAction<boolean>>
  closeModal: () => void
  requestAvailability: RequestAvailabilityFunction
}

export const cantidadOptions = [
  {
    value: 1,
    label: '1',
  },
  {
    value: 2,
    label: '2',
  },
  {
    value: 3,
    label: '3',
  },
  {
    value: 4,
    label: '4',
  },
]

export default function RequestTyre({
  selectedNeumatico,
  successMessage,
  setSuccessMessage,
  closeModal,
  requestAvailability,
}: Props) {
  const {
    register,
    handleSubmit,
    errors,
    setError,
    clearError,
    triggerValidation,
  } = useForm<EmailData>({
    mode: 'onChange',
    validationSchema: EmailSchema,
  })
  const [loading, setLoading] = useState(false)
  const device = Size.useMedia({
    mobile: 'MOBILE',
    tablet: 'TABLET',
    desktop: 'DESKTOP',
  }) as 'MOBILE' | 'TABLET' | 'DESKTOP'
  const isDesktop = Size.useMedia({
    mobile: false,
    tablet: true,
    desktop: true,
  })

  const onSubmit = (data) => {
    setLoading(true)
    const { email, cantidad } = data
    return requestAvailability({
      email,
      selectedNeumatico,
      device,
      precio: selectedNeumatico.precio?.toString() || '0', // Convert number to string
      idioma: locale,
      cantidad: parseInt(cantidad),
    })
      .then((res) => {
        if (res.errors) {
          res.errors.forEach((e) => {
            setError(e.field, e.type, t(e.message))
          })
          setLoading(false)
          return
        }
        setLoading(false)
        setSuccessMessage(true)
      })
      .catch((err) => {
        setLoading(false)
        Swal.fire(
          alertProp({
            type: 'error',
            title: 'Oops...',
            text: err.message,
          })
        )
      })
  }

  return (
    <section className={styles.container}>
      {loading && <SpinnerWithText text={t('alta_vehiculo.cargando')} />}
      {!successMessage && (
        <div className={loading ? styles.hidden : styles.email_input_container}>
          {selectedNeumatico && (
            <div className={styles.request_email_message}>
              <p>{t('landing_neumaticos.neumatico_item.pedir_info_mensaje')}</p>
              <div className={styles.marca_modelo_specs}>
                {selectedNeumatico.marca}&nbsp;
                {selectedNeumatico.modelo}
              </div>
              <div className={styles.medidas_specs}>
                {selectedNeumatico.descripcion || ''}
              </div>
            </div>
          )}

          <form
            onChange={(e) => {
              const target = e.target as HTMLInputElement
              // Revalidar cualquier campo cuando cambia su valor
              clearError(target.name as keyof EmailData)
              triggerValidation({
                name: target.name as keyof EmailData,
              })
            }}
            className={styles.request_neumatico_form}
            onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.title}>
              {t(
                'landing_neumaticos.neumatico_item.pedir_info_mensaje_contacto'
              )}
            </div>
            <div className={styles.form_group}>
              <div
                className={`${styles.form_field} ${styles.form_field_email}`}>
                <label>
                  <EmailIcon className={styles.calendar_icon} />
                  {t('login.email')}
                </label>
                <InputCesta
                  inputSize="big"
                  name="email"
                  refe={register}
                  errors={errors}
                  type="text"
                  errorBelow={true}
                />
              </div>

              <div
                className={`${styles.form_field} ${styles.form_field_cantidad}`}>
                <label>
                  <NeumaticoIcon className={styles.calendar_icon} />
                  Cantidad
                </label>
                <DropDown
                  name="cantidad"
                  options={cantidadOptions}
                  defaultSelected={cantidadOptions[1].label}
                  size={'xl'}
                  formRef={register}
                />
              </div>
            </div>
            <div className={styles.privacy_policy}>
              <input
                type="checkbox"
                id="privacy"
                name="checkboxPrivacy"
                ref={register}
              />
              <div>
                <span>{t('registerForm.checkbox-privacy-read')}</span>
                <a
                  className={styles.label_privacy_link}
                  href={route('politica-privacidad.index')}
                  target="_blank"
                  rel="noreferrer">
                  {t('registerForm.checkbox-privacy-policy')}
                </a>
              </div>
            </div>
            <FormError errors={errors} field="checkboxPrivacy" />
            <hr className={styles.solid} />
            <div className={styles.button_container}>
              {isDesktop && (
                <button
                  className={`${styles.button} ${styles.explorar}`}
                  onClick={() => closeModal()}>
                  {t(
                    'catalogo.producto.ficha.aviso_disponibilidad.modal.seguir_explorando_button'
                  )}
                </button>
              )}
              <button className={`${styles.button} ${styles.avisadme}`}>
                {t(
                  'catalogo.producto.ficha.aviso_disponibilidad.modal.avisadme_button'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
      {successMessage && (
        <div className={styles.send_email_success}>
          <Done />
          <div className={styles.email_success_message}>
            {t('landing_neumaticos.solicitar')}
          </div>
        </div>
      )}
    </section>
  )
}
